// Service Worker 등록 유틸리티 - v4 (강제 업데이트 지원)

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

// 새로고침 중복 방지
let isRefreshing = false;

// 현재 예상 SW 버전 (service-worker.js의 SW_VERSION과 일치해야 함)
const EXPECTED_SW_VERSION = 'v10';

// 먼저 서비스 워커 파일 접근 가능 여부 확인
async function checkServiceWorkerAvailable(swUrl: string): Promise<boolean> {
  try {
    const response = await fetch(swUrl, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

// 모든 서비스 워커 정리 (오류 복구용)
async function cleanupServiceWorkers(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
      console.log('[SW] Unregistered broken service worker');
    }
    // 모든 캐시도 삭제
    const cacheNames = await caches.keys();
    for (const cacheName of cacheNames) {
      await caches.delete(cacheName);
      console.log('[SW] Deleted cache:', cacheName);
    }
  } catch (error) {
    console.error('[SW] Cleanup error:', error);
  }
}

// SW 버전 확인 및 강제 업데이트
async function checkAndForceUpdateSW(): Promise<boolean> {
  if (!navigator.serviceWorker.controller) {
    return false; // SW가 없으면 체크 불필요
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    const timeout = setTimeout(() => {
      console.log('[SW] Version check timeout - forcing update');
      resolve(true); // 타임아웃 시 강제 업데이트
    }, 3000);

    messageChannel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      const currentVersion = event.data?.version;
      console.log(`[SW] Current version: ${currentVersion}, Expected: ${EXPECTED_SW_VERSION}`);

      if (currentVersion !== EXPECTED_SW_VERSION) {
        console.log('[SW] Version mismatch - forcing cleanup and update');
        resolve(true);
      } else {
        resolve(false);
      }
    };

    try {
      navigator.serviceWorker.controller!.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );
    } catch (error) {
      clearTimeout(timeout);
      console.error('[SW] Version check error:', error);
      resolve(true); // 오류 시 강제 업데이트
    }
  });
}

export function register(config?: Config) {
  if ('serviceWorker' in navigator) {
    // 프로덕션 환경에서만 또는 localhost에서 테스트
    const isLocalhost = Boolean(
      window.location.hostname === 'localhost' ||
      window.location.hostname === '[::1]' ||
      window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
    );

    if (process.env.NODE_ENV === 'production' || isLocalhost) {
      window.addEventListener('load', async () => {
        const swUrl = '/service-worker.js';

        // 1. 먼저 기존 SW 버전 확인 - 버전 불일치 시 강제 정리
        const needsForceUpdate = await checkAndForceUpdateSW();
        if (needsForceUpdate) {
          console.log('[SW] Force updating service worker...');
          await cleanupServiceWorkers();
          // 강제 새로고침으로 새 SW 적용
          if (!isRefreshing) {
            isRefreshing = true;
            window.location.reload();
            return;
          }
        }

        // 2. 서비스 워커 파일 접근 가능 여부 확인
        const isAvailable = await checkServiceWorkerAvailable(swUrl);

        if (isAvailable) {
          registerValidSW(swUrl, config);
        } else {
          // 서비스 워커 파일 접근 불가 - 기존 SW 정리하고 앱은 정상 실행
          console.warn('[SW] Service worker file not accessible, cleaning up...');
          await cleanupServiceWorkers();
          console.log('[SW] App will run without service worker');
        }
      });
    }
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl, { updateViaCache: 'none' }) // 캐시 없이 항상 새로 가져옴
    .then((registration) => {
      console.log('[SW] Service Worker registered successfully');

      // 즉시 업데이트 확인
      registration.update();

      // 주기적으로 업데이트 확인 (5분마다)
      setInterval(() => {
        registration.update();
      }, 5 * 60 * 1000);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // 새 콘텐츠가 있고, 이전 SW가 있음
              console.log('[SW] New content is available; activating immediately...');

              // 새 서비스 워커에게 즉시 활성화 요청
              installingWorker.postMessage({ type: 'SKIP_WAITING' });

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // 콘텐츠가 오프라인 사용을 위해 캐시됨
              console.log('[SW] Content is cached for offline use.');

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('[SW] Error during service worker registration:', error);
    });
}

// 서비스 워커 메시지 리스너 - SW_UPDATED 처리
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SW_UPDATED') {
      console.log(`[SW] Updated to version: ${event.data.version}`);
      if (!isRefreshing) {
        isRefreshing = true;
        window.location.reload();
      }
    }
  });

  // 서비스 워커 컨트롤러 변경 감지 - 즉시 새로고침
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('[SW] Controller changed, reloading...');
    if (!isRefreshing) {
      isRefreshing = true;
      window.location.reload();
    }
  });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// 지도 캐시 크기 조회
export async function getMapCacheSize(): Promise<number> {
  if ('serviceWorker' in navigator) {
    const controller = navigator.serviceWorker.controller;
    if (controller) {
      return new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          resolve(event.data.count || 0);
        };
        controller.postMessage(
          { type: 'GET_CACHE_SIZE' },
          [messageChannel.port2]
        );
      });
    }
  }
  return 0;
}

// 지도 캐시 삭제
export async function clearMapCache(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    const controller = navigator.serviceWorker.controller;
    if (controller) {
      return new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          resolve(event.data.success || false);
        };
        controller.postMessage(
          { type: 'CLEAR_MAP_CACHE' },
          [messageChannel.port2]
        );
      });
    }
  }
  return false;
}
